import { useState, useCallback, useRef, useEffect } from "react";
import { GetConditionByOrganization } from "../GetConditionByOrganization";

interface UseConditionByOrganizationProps {
  /** Organization id sem prefixo, ex: 05490544000100 */
  organizationId: string;
  /** Data inicial YYYY-MM-DD (opcional) */
  startDate?: string;
  /** Data final YYYY-MM-DD (opcional) */
  endDate?: string;
  /** Quantidade de registros (default 100) */
  count?: number;
  /** Incluir Encounter no bundle */
  includeEncounter?: boolean;

  /** Indica se deve retornar apenas o resumo (opcional) */
  summary?: boolean;

  /** Incluir Patient no bundle */
  includePatient?: boolean;
}

interface UseConditionByOrganizationResult {
  data: {
    conditions: fhir5.Condition[];
    total?: number;
    patients?: fhir5.Patient[];
  };
  loading: boolean;
  error: Error | null;
  fetchConditions: () => Promise<void>;
}

function extractConditions(bundle?: fhir5.Bundle): fhir5.Condition[] {
  if (!bundle?.entry) return [];
  return bundle.entry
    .filter(
      (entry): entry is fhir5.BundleEntry<fhir5.Condition> =>
        entry.resource?.resourceType === "Condition"
    )
    .map((entry) => entry.resource!)
    .filter((resource): resource is fhir5.Condition => resource !== undefined);
}

export function extractPatients(bundle?: fhir5.Bundle): fhir5.Patient[] {
  if (!bundle?.entry) return [];
  return bundle.entry
    .filter(
      (entry): entry is fhir5.BundleEntry<fhir5.Patient> =>
        entry.resource?.resourceType === "Patient"
    )
    .map((entry) => entry.resource!)
    .filter((resource): resource is fhir5.Patient => resource !== undefined);
}

export function useConditionByOrganization(
  props: UseConditionByOrganizationProps
): UseConditionByOrganizationResult {
  const [data, setData] = useState<{
    conditions: fhir5.Condition[];
    patients?: fhir5.Patient[];
    total?: number;
  }>({
    conditions: [],
    patients: [],
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const paramsRef = useRef(props);

  useEffect(() => {
    paramsRef.current = props;
  }, [props]);

  const fetchConditions = useCallback(async () => {
    const currentParams = paramsRef.current;
    if (!currentParams.organizationId) return;
    try {
      setLoading(true);
      setError(null);
      
      // Primeiro busca o count total
      const countResponse = await GetConditionByOrganization({
        ...currentParams,
        summary: true,
        count: 0,
      });
      
      const totalCount = countResponse.total || 0;
      console.log(`Total de Conditions encontrados: ${totalCount}`);
      
      // Agora busca todos os registros com o count adequado
      const response = await GetConditionByOrganization({
        ...currentParams,
        count: totalCount > 0 ? totalCount : 1000,
        summary: false,
      });
      
      const conditions = extractConditions(response);
      const patients = extractPatients(response);
      setData({ conditions, patients, total: totalCount });
    } catch (err) {
      setError(
        err instanceof Error ? err : new Error("Erro ao buscar Conditions")
      );
      console.error("useConditionByOrganization error:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  return { data, loading, error, fetchConditions };
}
